// QR rendering for the commons deposit address + the /get install link.
//
// Encoding is delegated to `qrcode-generator` (Kazuhiko Arase, MIT) — a
// battle-tested, dependency-free encoder bundled at build time (no network at
// runtime, satisfying M3 decision 6). It replaced a hand-rolled encoder whose
// format-information bits produced a structurally-valid but non-scannable
// matrix. We keep our own SVG rendering so the Fire-Beacon palette and the
// self-sizing (square, never squashed) behaviour stay under our control.
import qrcode from "qrcode-generator";

export interface QrMatrix {
  size: number;
  modules: boolean[][];
}

// buildMatrix encodes `text` (byte mode, ECC level M, smallest fitting version)
// into a boolean module grid. Throws if the encoder cannot fit the payload.
export function buildMatrix(text: string): QrMatrix {
  const qr = qrcode(0 /* auto type number */, "M");
  qr.addData(text, "Byte");
  qr.make();
  const size = qr.getModuleCount();
  const modules: boolean[][] = Array.from({ length: size }, (_, r) =>
    Array.from({ length: size }, (_, c) => qr.isDark(r, c)),
  );
  return { size, modules };
}

// qrSvg renders `text` as an inline SVG QR. The inline style keeps it square
// (width:100%, height:auto over the 1:1 viewBox) so a call site that forgets to
// size it in CSS can never squash it to the replaced-element default (300×150),
// which would make it unscannable. Call-site CSS may still scale the width.
export function qrSvg(
  text: string,
  opts: { quiet?: number; dark?: string; light?: string } = {},
): string {
  const { size, modules } = buildMatrix(text);
  const quiet = opts.quiet ?? 4;
  const dark = opts.dark ?? "#1a1714";
  const light = opts.light ?? "#ece6d6";
  const dim = size + quiet * 2;
  let path = "";
  for (let r = 0; r < size; r++)
    for (let c = 0; c < size; c++)
      if (modules[r][c]) path += `M${c + quiet} ${r + quiet}h1v1h-1z`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" ` +
    `style="display:block;width:100%;height:auto" ` +
    `shape-rendering="crispEdges" role="img" aria-label="QR code">` +
    `<rect width="${dim}" height="${dim}" fill="${light}"/>` +
    `<path d="${path}" fill="${dark}"/></svg>`
  );
}
